import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Container from "@material-ui/core/Container";
import { makeStyles } from "@material-ui/core/styles";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import Checkbox from "@material-ui/core/Checkbox";
import Box from "@material-ui/core/Box";
import TablePagination from "@material-ui/core/TablePagination";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import DeleteIcon from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";
import EditIcon from "@material-ui/icons/Edit";
import {
  addAdmin,
  listAdmin,
  updateAdmin,
  deleteAdmin,
} from "../../store/adminMaster";
import EnhancedTableHead from "../../components/common/table/EnhancedTableHead";
import { showCustomConfirmationDialog } from "../../store/customConfirmationDialogReducer";
import Layout from "../../components/common/hoc/Layout";
import CustomSpinner from "../../components/common/Spinner/CustomSpinner";
import Add from "./components/add";
import Edit from "./components/edit";
import NotFound from "../../components/common/notFound/NotFound";
import RecordChangeButtons from "../../components/common/recordChangeButtons/RecordChangeButtons";
import { getComparator, stableSort } from "../../utils/tableFilter";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    marginTop: "1rem",
    minWidth: 750,
  },
  rowEnable: {
    outlineColor: theme.palette.secondary.main,
    outlineWidth: "2px",
    outlineStyle: "solid",
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-start",
    width: "100%",
  },
  buttonAdd: {
    marginLeft: theme.spacing(1),
  },
}));

const RegisterAdmin = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation("translation");
  const adminMasterReducer = useSelector((state) => state.adminMasterReducer);
  const { loading, error, admin } = adminMasterReducer;
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [selected, setSelected] = useState([]);
  const [rowEnable, setRowEnable] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [email, setEmail] = useState("");
  const [isEnabledFormAddAdmin, setIsEnabledFormAddAdmin] = useState(false);
  const [isEnabledFormEditAdmin, setIsEnabledFormEditAdmin] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState();
  const headCells = [
    {
      id: "name",
      numeric: false,
      disablePadding: true,
      label: t("registerAdmin.headCells.name"),
    },
    {
      id: "role",
      numeric: false,
      disablePadding: true,
      label: t("registerAdmin.headCells.role"),
    },
    {
      id: "email",
      numeric: false,
      disablePadding: false,
      label: t("registerAdmin.headCells.email"),
    },
  ];
  const isSelected = (name) => selected.indexOf(name) !== -1;
  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, admin?.length - page * rowsPerPage);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = admin.map((n) => n);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const toggleFormAdmin = () => {
    setIsEnabledFormAddAdmin((prev) => !prev);
    setIsEnabledFormEditAdmin(false);
  };

  const toggleFormEdit = (isEnable) => {
    setIsEnabledFormAddAdmin(false);
    setIsEnabledFormEditAdmin(isEnable);
  };

  const focusOnlastItemAdded = () => {
    setRowEnable(true);
    setTimeout(function () {
      setRowEnable(false);
    }, 3000);
  };

  const addHandler = async (formValues) => {
    setEmail(formValues.email);
    await dispatch(addAdmin(formValues.name, formValues.email));
    await dispatch(listAdmin());
    setSelected([]);
    focusOnlastItemAdded();
  };

  const deleteHandler = (admin) => {
    const configDialog = {
      title: t("registerAdmin.deleteDialog.title"),
      message: `${t("registerAdmin.deleteDialog.firstMessage")} "${
        admin.name
      }", ${t("registerAdmin.deleteDialog.secondMessage")}`,
      onSubmit: async function () {
        await dispatch(deleteAdmin(admin))
          .then(async (result) => {
            await dispatch({ type: "CUSTOM_CONFIRMATION_DIALOG_CLOSED" });
            await dispatch(listAdmin());
            setSelected([]);
          })
          .catch((err) => {
            dispatch({
              type: "ADMIN_DELETE_FAIL",
              payload: error,
            });
          });
      },

      close: async function () {
        await dispatch({ type: "CUSTOM_CONFIRMATION_DIALOG_CLOSED" });
      },
      buttons: {
        cancel: t("registerAdmin.deleteDialog.buttons.cancel"),
        confirm: t("registerAdmin.deleteDialog.buttons.confirm"),
      },
    };

    dispatch(
      showCustomConfirmationDialog({
        ...configDialog,
      })
    );
  };

  const editHandler = (admin) => {
    toggleFormEdit(false);
    setCurrentAdmin(admin);
    const configDialog = {
      title: t("registerAdmin.editDialog.title"),
      message: `${t("registerAdmin.editDialog.firstMessage")} "${
        admin.name
      }", ${t("registerAdmin.editDialog.secondMessage")}`,
      onSubmit: function () {
        toggleFormEdit(true);
      },
      close: async function () {
        await dispatch({ type: "CUSTOM_CONFIRMATION_DIALOG_CLOSED" });
      },
      buttons: {
        cancel: t("registerAdmin.editDialog.buttons.cancel"),
        confirm: t("registerAdmin.editDialog.buttons.confirm"),
      },
    };
    dispatch(
      showCustomConfirmationDialog({
        ...configDialog,
      })
    );
  };

  const updateHandler = async (formValues) => {
    setEmail(formValues.email);
    await dispatch(
      updateAdmin(currentAdmin.id, formValues.name, formValues.email)
    );
    await dispatch(listAdmin());
    setSelected([]);
    focusOnlastItemAdded();
  };

  const formConfigI18n = {
    singleDeleteButton: t("registerAdmin.singleDeleteButton"),
    singleDeleteMultipleButton: t("registerAdmin.singleDeleteMultipleButton"),
    addForm: {
      title: t("registerAdmin.addForm.title"),
      cancelButton: t("registerAdmin.addForm.cancelButton"),
      confirmButton: t("registerAdmin.addForm.confirmButton"),
      name: {
        requiredErrorMsg: t("registerAdmin.addForm.name.requiredErrorMsg"),
        label: t("registerAdmin.addForm.name.label"),
        placeholder: t("registerAdmin.addForm.name.placeholder"),
      },
      email: {
        requiredErrorMsg: t("registerAdmin.addForm.email.requiredErrorMsg"),
        label: t("registerAdmin.addForm.email.label"),
        placeholder: t("registerAdmin.addForm.email.placeholder"),
      },
    },
    editForm: {
      title: t("registerAdmin.editForm.title"),
      cancelButton: t("registerAdmin.editForm.cancelButton"),
      confirmButton: t("registerAdmin.editForm.confirmButton"),
      name: {
        requiredErrorMsg: t("registerAdmin.editForm.name.requiredErrorMsg"),
        label: t("registerAdmin.editForm.name.label"),
        placeholder: t("registerAdmin.editForm.name.placeholder"),
      },
      email: {
        requiredErrorMsg: t("registerAdmin.editForm.email.requiredErrorMsg"),
        label: t("registerAdmin.editForm.email.label"),
        placeholder: t("registerAdmin.editForm.email.placeholder"),
      },
    },
  };

  useEffect(() => {
    dispatch(listAdmin());
  }, [dispatch]);

  return (
    <Layout title={t("registerAdmin.title")}>
      <Container disableGutters maxWidth={false}>
        <Box
          component="span"
          display={
            isEnabledFormAddAdmin || isEnabledFormEditAdmin ? "none" : "block"
          }
        >
          <Grid item xs={12} sm={12} lg={12}>
            <div className={classes.buttons}>
              <Button
                variant="contained"
                color="primary"
                disabled={loading}
                onClick={() => setIsEnabledFormAddAdmin((prev) => !prev)}
              >
                {t("registerAdmin.addButton")}
              </Button>
            </div>
          </Grid>
        </Box>

        <Add
          title={formConfigI18n.addForm.title}
          cancelButton={formConfigI18n.addForm.cancelButton}
          confirmButton={formConfigI18n.addForm.confirmButton}
          name={formConfigI18n.addForm.name}
          email={formConfigI18n.addForm.email}
          isEnabledFormAddAdmin={!isEnabledFormAddAdmin}
          loading={loading}
          addHandler={addHandler}
          show={toggleFormAdmin}
        />

        <Edit
          title={formConfigI18n.editForm.title}
          cancelButton={formConfigI18n.editForm.cancelButton}
          confirmButton={formConfigI18n.editForm.confirmButton}
          name={formConfigI18n.editForm.name}
          email={formConfigI18n.editForm.email}
          currentAdmin={currentAdmin}
          isEnabledFormEditAdmin={!isEnabledFormEditAdmin}
          loading={loading}
          editHandler={updateHandler}
          show={toggleFormEdit}
        />

        <>
          {loading ? (
            <CustomSpinner />
          ) : error ? (
            <NotFound
              title="Oops!"
              message="Ocorreu um erro, tente recarregar a página"
              image="/images/notfound.png"
              redirect="/app/content"
            />
          ) : (
            <div>
              <TableContainer>
                <Table
                  className={classes.table}
                  aria-labelledby="tableTitle"
                  size={"medium"}
                  aria-label="enhanced table"
                >
                  <EnhancedTableHead
                    headCells={headCells}
                    classes={classes}
                    numSelected={selected.length}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={admin?.length}
                  />
                  <TableBody>
                    {stableSort(admin, getComparator(order, orderBy))
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                      .map((row, index) => {
                        const isItemSelected = isSelected(row);
                        const labelId = `enhanced-table-checkbox-${index}`;

                        return (
                          <TableRow
                            hover
                            role="checkbox"
                            aria-checked={isItemSelected}
                            tabIndex={0}
                            key={row.id}
                            selected={isItemSelected}
                            className={
                              row.email === email && rowEnable
                                ? classes.rowEnable
                                : ""
                            }
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                onClick={(event) => handleClick(event, row)}
                                checked={isItemSelected}
                                inputProps={{ "aria-labelledby": labelId }}
                              />
                              <IconButton
                                aria-label="edit"
                                onClick={(event) => editHandler(row)}
                              >
                                <EditIcon color="secondary" />
                              </IconButton>
                            </TableCell>
                            <TableCell padding="none">{row.name}</TableCell>
                            <TableCell align="left">{row.role}</TableCell>
                            <TableCell align="left">{row.email}</TableCell>
                          </TableRow>
                        );
                      })}
                    {emptyRows > 0 && (
                      <TableRow style={{ height: 53 * emptyRows }}>
                        <TableCell colSpan={6} />
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box display="flex" p={1}>
                <Box p={1} flexGrow={1}>
                  <TablePagination
                    rowsPerPageOptions={[10, 25]}
                    component="div"
                    count={admin?.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                  />
                </Box>
                <Box p={1} flexGrow={1}>
                  <RecordChangeButtons
                    singleDeleteButton={formConfigI18n.singleDeleteButton}
                    singleDeleteMultipleButton={
                      formConfigI18n.singleDeleteMultipleButton
                    }
                    itensSelected={selected}
                    handlerSingle={deleteHandler}
                    icon={<DeleteIcon />}
                  />
                </Box>
              </Box>
            </div>
          )}
        </>
      </Container>
    </Layout>
  );
};

export { RegisterAdmin };
